import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, CircularProgress, Skeleton, TablePagination,
  Snackbar, Alert, InputAdornment, Tooltip, Avatar, Paper, Grid
} from "@mui/material";
import { Plus, Pencil, Trash2, X, Briefcase, Type, AlignLeft, Layers, Tag, Upload, Hash, Link, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetServices,
  adminGetServiceById,
  adminCreateService,
  adminUpdateService,
  adminDeleteService,
  adminGetServiceCategories,
} from "../../api/services";

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];
const STATUS_STYLES = {
  ACTIVE:   { bg: "rgba(34,197,94,0.1)",  color: "#16A34A" },
  DRAFT:    { bg: "rgba(245,158,11,0.1)", color: "#B45309" },
  INACTIVE: { bg: "rgba(239,68,68,0.1)",  color: "#DC2626" },
};

const slugify = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const DEFAULT_VALUES = {
  name: "",
  slug: "",
  short_description: "",
  detailed_description: "",
  category: "",
  icon: "",
  order: 0,
  status: "DRAFT",
  card_image: null,
};

export default function ServicesAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const watchName = watch("name", "");
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Fetch services
  const { data: apiServices = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["admin-services"],
    queryFn: () => adminGetServices().then(r => r.data),
  });

  // Fetch categories
  const { data: apiCategories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ["admin-service-categories"],
    queryFn: () => adminGetServiceCategories().then(r => r.data),
  });

  const services = Array.isArray(apiServices) ? apiServices : [];
  const categories = Array.isArray(apiCategories) ? apiCategories : [];
  const categoryOptions = categories.map(c => ({
    label: c.name,
    value: c.id.toString(),
  }));

  const filteredServices = services.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.short_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.category && categories.find(c => c.id.toString() === s.category.toString())?.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  const paginatedServices = filteredServices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalCount = filteredServices.length;

  const createMutation = useMutation({
    mutationFn: (fd) => adminCreateService(fd),
    onSuccess: () => {
      qc.invalidateQueries(["admin-services"]);
      notify("Service created");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create service", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateService(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-services"]);
      notify("Service updated");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update service", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteService,
    onSuccess: () => {
      qc.invalidateQueries(["admin-services"]);
      notify("Service deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(err?.response?.data?.message || "Couldn't delete service", "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const fd = new FormData();
    fd.append("name", values.name);
    fd.append("slug", values.slug?.trim() || slugify(values.name));

    ["short_description", "detailed_description", "icon", "status"].forEach(k => {
      if (values[k] !== undefined && values[k] !== "") fd.append(k, values[k]);
    });

    if (values.category) fd.append("category", values.category);
    if (values.order !== "" && values.order != null) fd.append("order", values.order);

    if (values.card_image?.[0]) fd.append("card_image", values.card_image[0]);

    if (editing) updateMutation.mutate({ id: editing.id, data: fd });
    else createMutation.mutate(fd);
  };

  const openCreate = () => {
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = async (s) => {
    setEditLoading(true);
    try {
      const { data } = await adminGetServiceById(s.id);
      setEditing(data);
      setThumbPreview(data.card_image_url || null);
      reset({
        name: data.name ?? "",
        slug: data.slug ?? "",
        short_description: data.short_description ?? "",
        detailed_description: data.detailed_description ?? "",
        category: data.category?.toString() ?? "",
        icon: data.icon ?? "",
        order: data.order ?? 0,
        status: data.status ?? "DRAFT",
      });
      setOpen(true);
    } catch (err) {
      notify(err?.response?.data?.message || "Couldn't load service details", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    setThumbPreview(null);
    reset(DEFAULT_VALUES);
  };

  const handleThumbChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setThumbPreview(URL.createObjectURL(file));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  // Find category name by ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return null;
    const category = categories.find(c => c.id.toString() === categoryId.toString());
    return category?.name || categoryId;
  };

  return (
    <AdminShell title="Services">
      {/* Page header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5, mb: 2.5,
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Services listed on the public site. Set status to Active to make a service visible to visitors.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Service
        </Button>
      </Box>

      {/* Search Bar */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 2, mb: 2.5,
      }}>
        <TextField
          fullWidth
          placeholder="Search services by name, description, or category..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94A3B8" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              border: "1px solid rgba(15,23,42,0.15)",
              "&:hover": { borderColor: "#2563EB" },
            },
          }}
        />
      </Card>

      {/* Table Card */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden"
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Service", "Category", "Order", "Status", ""].map(h => (
                  <TableCell key={h} sx={{
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.03em",
                    textTransform: "uppercase", color: "text.secondary",
                    borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap"
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {servicesLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 200 : 80} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: "14px",
                      bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 1.5
                    }}>
                      <Briefcase size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      No services found
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Add your first service to publish it on the public site."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedServices.map((s) => {
                const statusStyle = STATUS_STYLES[s.status] || STATUS_STYLES.DRAFT;
                const categoryName = getCategoryName(s.category);
                return (
                  <TableRow key={s.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          variant="rounded"
                          src={s.card_image_url || undefined}
                          sx={{ width: 38, height: 38, borderRadius: "9px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", flexShrink: 0 }}
                        >
                          <Briefcase size={16} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                            {s.name}
                          </Typography>
                          {s.short_description && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2 }} noWrap>
                              {s.short_description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {categoryName ? (
                        <Chip
                          label={categoryName}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: 11, height: 22, bgcolor: "rgba(37,99,235,0.07)", color: "#2563EB" }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {s.order ?? "—"}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={s.status || "DRAFT"}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEdit(s)}
                              disabled={editLoading}
                              sx={{
                                border: "1px solid rgba(15,23,42,0.1)",
                                borderRadius: "8px",
                                "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                              }}
                            >
                              {editLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(s)}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid rgba(15,23,42,0.08)",
            "& .MuiTablePagination-toolbar": { padding: "16px 24px" },
            "& .MuiTablePagination-selectLabel": {
              fontSize: "13.5px", color: "text.secondary", marginRight: "8px",
            },
            "& .MuiTablePagination-displayedRows": { fontSize: "13.5px", color: "text.secondary" },
            "& .MuiInputBase-root": {
              borderRadius: "8px", border: "1px solid rgba(15,23,42,0.15)",
            },
            "& .MuiTablePagination-actions": { gap: "4px" },
            "& .MuiIconButton-root": {
              borderRadius: "8px", border: "1px solid rgba(15,23,42,0.1)", width: "32px", height: "32px",
            },
          }}
        />
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 30px 70px rgba(15,23,42,0.22)",
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2.5,
          px: 3,
          background: "linear-gradient(135deg, #7C3AED 0%, #2563EB 55%, #1D4ED8 100%)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <Briefcase size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {editing ? "Edit Service" : "Add New Service"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", fontWeight: 500, mt: 0.25 }}>
                {editing ? `Editing: ${editing.name}` : "Create a new service for your catalog"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: 3,
            pb: 1,
            px: 3,
            maxHeight: "65vh",
            overflowY: "auto",
            backgroundColor: "#F1F5F9",
          }}>
            {/* Basic Information */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #7C3AED",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(124,58,237,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Type size={17} color="#7C3AED" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Basic Information
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Service Name"
                    fullWidth
                    disabled={saving}
                    required
                    error={!!errors.name}
                    helperText={errors.name ? "Name is required" : ""}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Type size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("name", { required: true })}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Slug"
                    fullWidth
                    disabled={saving}
                    placeholder={watchName ? slugify(watchName) : "auto-generated from name"}
                    helperText="Leave blank to auto-generate. Lowercase letters, numbers, hyphens only."
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Link size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("slug")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Short Description"
                    fullWidth
                    multiline
                    rows={2}
                    disabled={saving}
                    placeholder="Brief summary shown on service cards"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><AlignLeft size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("short_description")}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Detailed Description"
                    fullWidth
                    multiline
                    rows={4}
                    disabled={saving}
                    placeholder="Full service description for the detail page"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                        <AlignLeft size={16} color="#94A3B8" />
                      </InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("detailed_description")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Category & Icon */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #16A34A",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(22,163,74,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Layers size={17} color="#16A34A" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Category & Icon
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Category"
                        fullWidth
                        disabled={saving || categoriesLoading}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Layers size={16} color="#94A3B8" /></InputAdornment>
                        }}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {categoryOptions.map(c => (
                          <MenuItem key={c.value} value={c.value}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: "50%",
                                bgcolor: "rgba(37,99,235,0.15)"
                              }} />
                              {c.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Icon"
                    fullWidth
                    disabled={saving}
                    placeholder="e.g. cloud, shield, code (icon name or URL)"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Link size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("icon")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Display Settings */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #EA580C",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(234,88,12,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Tag size={17} color="#EA580C" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Display Settings
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Display Order"
                    type="number"
                    fullWidth
                    disabled={saving}
                    placeholder="0 = first"
                    inputProps={{ min: 0, step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Hash size={16} color="#94A3B8" /></InputAdornment>
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("order", { min: 0 })}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Status"
                        fullWidth
                        disabled={saving}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>
                        }}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        {STATUSES.map(s => (
                          <MenuItem key={s} value={s}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Box sx={{
                                width: 8, height: 8, borderRadius: "50%",
                                bgcolor: STATUS_STYLES[s]?.bg || "rgba(15,23,42,0.08)"
                              }} />
                              {s}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Thumbnail */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #2563EB",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(37,99,235,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Briefcase size={17} color="#2563EB" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Thumbnail
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: "12px",
                    border: "2px dashed rgba(37,99,235,0.2)",
                    backgroundColor: "rgba(37,99,235,0.03)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#2563EB",
                      backgroundColor: "rgba(37,99,235,0.05)"
                    }
                  }}>
                    <Avatar
                      variant="rounded"
                      src={thumbPreview || undefined}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "12px",
                        bgcolor: "rgba(37,99,235,0.08)",
                        color: "#2563EB",
                        flexShrink: 0
                      }}
                    >
                      <Briefcase size={24} />
                    </Avatar>
                    <Box>
                      <Button
                        component="label"
                        variant="contained"
                        size="small"
                        disabled={saving}
                        startIcon={<Upload size={14} />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: "8px",
                          bgcolor: "#2563EB",
                          "&:hover": { bgcolor: "#1D4ED8" }
                        }}
                      >
                        {thumbPreview ? "Change Image" : "Upload Image"}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          {...register("card_image")}
                          onChange={(e) => {
                            register("card_image").onChange(e);
                            handleThumbChange(e);
                          }}
                        />
                      </Button>
                      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                        Recommended: 400x250px, JPG/PNG
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "#FFFFFF",
            display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
          }}>
            <Button
              onClick={closeDialog}
              disabled={saving}
              sx={{
                textTransform: "none", fontWeight: 700, color: "#475569",
                px: 2.5, py: 1, borderRadius: "10px",
                backgroundColor: "rgba(15,23,42,0.04)",
                "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)",
                px: 3.5, py: 1.1, minWidth: 140,
                boxShadow: "0 4px 14px rgba(124,58,237,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #6D28D9 0%, #1D4ED8 100%)",
                  boxShadow: "0 6px 18px rgba(124,58,237,0.4)"
                },
              }}
            >
              {saving ? "Saving..." : editing ? "Update Service" : "Create Service"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this service?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.name}" will be removed from the public site immediately. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          px: 3, pb: 2.5,
          borderTop: "1px solid rgba(15,23,42,0.08)",
        }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}